import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, SparklesIcon } from 'lucide-react';
interface ExecutiveSummaryProps {
  open: boolean;
  onClose: () => void;
}
export function ExecutiveSummary({
  open,
  onClose
}: ExecutiveSummaryProps) {
  return <AnimatePresence data-id="element-26" data-mp="217k7hlu9cinz">
      {open && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-id="element-27" data-mp="1wbwk9bzc5zl4">
          <motion.div initial={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.96,
        opacity: 0,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-28" data-mp="1id5ag1rib02s">
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-10 py-5 border-b border-slate-200 bg-white sticky top-0 z-10" data-id="element-29" data-mp="2eicklk7cec24">
              <div className="flex items-center gap-3" data-id="element-30" data-mp="1lu8dqvd1bsxu">
                <div className="w-10 h-10 rounded-xl bg-[#6C2BD9] flex items-center justify-center" data-id="element-31" data-mp="1lv07e0n2nglh">
                  <SparklesIcon className="w-5 h-5 text-white" data-id="element-32" data-mp="1qs9vncyq5m7q" />
                </div>
                <div data-id="element-33" data-mp="1uvy7i7ue33ys">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#6C2BD9] font-semibold" data-id="element-34" data-mp="1ut25a7bidt4j">
                    Snakker · Investor Pitch
                  </div>
                  <h2 className="text-lg md:text-xl font-bold text-slate-900 leading-tight" data-id="element-35" data-mp="3690j0ohmt3c3">
                    Executive Summary
                  </h2>
                </div>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors" aria-label="Close" data-id="element-36" data-mp="125p7ueh7yeoj">
                <XIcon className="w-5 h-5 text-slate-600" data-id="element-37" data-mp="1b52j9cowldbw" />
              </button>
            </div>

            <article className="overflow-y-auto px-6 md:px-12 py-10 prose prose-slate max-w-none" data-id="element-38" data-mp="nfi0atsr2g2m">
              <p className="text-xl md:text-2xl font-semibold text-slate-900 leading-snug mb-8" data-id="element-39" data-mp="jdwgsplpn3jg">
                Snakker is the AI-native engine for{' '}
                <span className="text-[#6C2BD9]" data-id="element-40" data-mp="sfmico9b3iuy">
                  Legacy Brand Optimization
                </span>{' '}
                — unlocking value from pharma's revenue tail through continuous,
                scalable optimization of mature and off-patent brands.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-41" data-mp="2bf3kxsbctgj0">
                The Pharma Lifecycle
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-42" data-mp="2g7wr8kmh0wx7">
                Every pharma product moves through four phases.{' '}
                <strong data-id="element-43" data-mp="27d8l6s92nb7c">R&D</strong> runs deep into the negative through
                discovery and clinical development. <strong data-id="element-44" data-mp="2v32x97lkgvqj">Launch</strong> is
                the rapid rise as the brand reaches market and starts generating
                revenue. <strong data-id="element-45" data-mp="2z4efut60wgp8">Mature</strong> is the peak — strong share,
                engaged prescribers, and the cash that funds the rest of the
                portfolio. Then comes <strong data-id="element-46" data-mp="2q3gll2ldznju">Legacy</strong>: patents expire,
                attention shifts to newer assets, marketing investment is
                pulled, and revenue typically falls off a cliff and flattens
                into a thin tail just above zero. Snakker focuses entirely on
                this Legacy phase — the strategic move is to{' '}
                <em data-id="element-47" data-mp="1u0ucp8j7yua5">soften the cliff and extend the tail</em>, where small
                improvements compound into enormous portfolio value.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-48" data-mp="1l7h99cnj4a9t">
                The Market
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-49" data-mp="1g5i6o4zm5hdx">
                Roughly <strong data-id="element-50" data-mp="2cx28llqcdsss">30–35% of big pharma revenue</strong> comes from
                mature and legacy brands, and{' '}
                <strong data-id="element-51" data-mp="28tw7stm00ttp">$180–300B+ in revenue</strong> is facing loss of
                exclusivity in the coming years. This is the part of the
                portfolio that quietly funds the next generation of innovation —
                and it is the most under-optimized segment in the industry.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-52" data-mp="nixej2bbd19b">
                The Problem
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-53" data-mp="dg1xiar6lp96">
                When patents expire, generic competition can cause sales to drop
                more than 90% almost overnight for small molecules — and
                marketing investment is stopped immediately. Sales teams are
                reassigned, budgets cut, and when the gap between revenue and
                cost approaches zero the product is taken off the market. The
                real problem isn't that these brands lack value — it's that{' '}
                <strong data-id="element-54" data-mp="1164k4bcbu30e">traditional marketing is simply too expensive</strong>{' '}
                to justify on declining revenue, so brands die earlier than they
                need to. Biologics face the same end state, just on a slower
                curve.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-55" data-mp="37wlvvkdqxwjm">
                The Business Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-56" data-mp="34smui5wmaecu">
                Snakker takes over the annual <strong data-id="element-57" data-mp="2zul8pd7t40xo">campaign calendar</strong>{' '}
                for portfolios of legacy brands across multiple countries —
                typically <strong data-id="element-58" data-mp="3esx4zir9p7ob">3–4 campaigns per product per year</strong>.
                One contract per account, one operating partner, predictable
                annual revenue. Four accounts are already running this model:{' '}
                <strong data-id="element-59" data-mp="3npnybnefq8f7">GSK</strong> (Early Stage III — 5 products in Nigeria &
                Kenya, expanding to 7 Gulf countries),{' '}
                <strong data-id="element-60" data-mp="1cpf9ijlmg7n">Solventum</strong> (Late Stage II — MedSurge + Filltek
                divisions across UK, Germany, Nordics & South Africa),{' '}
                <strong data-id="element-61" data-mp="3skts18e2wrdu">Leo Pharma</strong> (Stage II — 5 products across UK,
                Brazil, Poland & Greece) and{' '}
                <strong data-id="element-62" data-mp="6bsn1aqyr0jb">Roche Pharmaceutical</strong> (Early Stage II — 3
                products across China, Canada, Italy & Germany). Each one
                represents a potential of <strong data-id="element-63" data-mp="oy04bzhqp8ob">100+ countries</strong> and{' '}
                <strong data-id="element-64" data-mp="txuvm45fg09w">10× the current brand count</strong>.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-65" data-mp="fzo5xbamt6lb">
                The Unlocked Value
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-66" data-mp="l0c6ooonx1sq">
                Even a <strong data-id="element-67" data-mp="68jqkah1njpy">1% improvement</strong> across a legacy portfolio
                represents tens of millions of euros per year. Modeled against a
                pharma division of €5–60B with 35% legacy revenue, the
                unmanaged, under-optimized value sitting on the table is
                enormous — and it compounds every quarter it's left alone.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-68" data-mp="3jgwm7qppnydx">
                Why AI Alone Isn't Enough
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-69" data-mp="7rlgt92pvmvh">
                Generative AI dramatically reduces the cost of content creation,
                making it theoretically possible to support the long tail. But
                AI alone is not enough.{' '}
                <strong data-id="element-70" data-mp="3uugfrtcw1v2q">Execution is the bottleneck</strong> — what actually
                moves legacy revenue is strategy, compliance, distribution and
                measurement working together as a system.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-71" data-mp="3g7ddl9dmey92">
                What Makes Us Structurally Different
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-72" data-mp="3b67n7i7fnn2p">
                Backed by an <strong data-id="element-73" data-mp="37808eswj4vu5">Innobooster grant</strong>, we have turned
                thirty years of pharma campaign models into AI workflows.
                Storyboarding and creation now happen in minutes, not months,
                producing short, engaging, TA-relevant campaigns ready for
                deployment. Four things set Snakker apart:{' '}
                <strong data-id="element-74" data-mp="1hpbdp8a85dpo">deep vertical expertise and master agreements</strong>{' '}
                in regulated environments where generalist AI fails; a{' '}
                <strong data-id="element-75" data-mp="1mpft0uzec2eb">one-to-one precision delivery model</strong> versus
                agencies' broad-stroke campaigns; a{' '}
                <strong data-id="element-76" data-mp="tzz3q672p1mb">proprietary interactive engagement platform</strong>{' '}
                that drives measurable outcomes; and a{' '}
                <strong data-id="element-77" data-mp="yzvbmgiw5d75">compounding behavioural flywheel</strong> in which data
                refines the model weekly, widening the gap over time.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-78" data-mp="17r5fp1rjgrq3">
                A New Category: Campaign Service Management
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-79" data-mp="fb314onscgt3">
                Snakker is not an agency and not a SaaS tool. We are creating a
                new category — <strong data-id="element-80" data-mp="2b8bfv2frqt75">Campaign Service Management</strong> —{' '}
                managed pharma campaign execution powered by software economics.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-81" data-mp="2k99l1o1y16rb">
                Three Levers
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-82" data-mp="2p82wzc1l0ppd">
                Our approach pulls three levers that compound on each other. We{' '}
                <strong data-id="element-83" data-mp="1wjoxssyrbnc8">reduce erosion</strong> through continuous, data-driven
                engagement that keeps HCPs connected to the brand. We{' '}
                <strong data-id="element-84" data-mp="21hyrt08qwhw9">increase profitability</strong> by replacing traditional
                marketing with AI-automated execution, widening the margin on
                every euro of legacy revenue. And we{' '}
                <strong data-id="element-85" data-mp="18srvm7wrk4e6">prolong lifespan</strong> — lower costs and slower
                erosion keep the gap between revenue and cost positive for
                longer, so products stay on market generating value instead of
                being withdrawn prematurely.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-86" data-mp="1190gmrfpgf4j">
                The Operating Model
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-87" data-mp="s9dh6b6jdje4">
                Snakker runs on four pillars. We{' '}
                <strong data-id="element-88" data-mp="ncwrvdnloyzg">leverage existing assets</strong> — no new content
                creation required, we repurpose approved materials into
                interactive formats. We achieve{' '}
                <strong data-id="element-89" data-mp="dm24u72j5vcy">rapid deployment</strong> through centralized creation
                with localized distribution. We perform{' '}
                <strong data-id="element-90" data-mp="4kujepl44p0a">behavioral data capture</strong> that moves beyond
                vanity metrics to reveal what actually drives HCP decisions. And
                we run a <strong data-id="element-91" data-mp="3vrninnwcie26">continuous optimization</strong> loop where
                every interaction informs the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-92" data-mp="3q4v53fx3mln5">
                What Snakker Does
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-93" data-mp="3c6wu8khzl7ce">
                Snakker acts as the operating layer between strategy and
                execution. We create campaigns from approved content, turn them
                into short interactive <strong data-id="element-94" data-mp="dnq3n8uu9d48">Snaks™</strong>, publish them
                across channels, capture behavioral data on every interaction,
                and improve campaign after campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-95" data-mp="3xn40pt7p0wx">
                Snaks Throughout the Customer Journey
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-96" data-mp="rlefxsaubbxo">
                The same Snak format adapts to every stage of the HCP journey:{' '}
                <strong data-id="element-97" data-mp="2h3th5fcw64rp">Awareness</strong> (condition framing, diagnostic
                prompts), <strong data-id="element-98" data-mp="2c5ruylk65pc0">Education</strong> (disease state, mechanism
                of action, preference exploration), <strong data-id="element-99" data-mp="2pxg9evhw4pw5">Engagement</strong>{' '}
                (live polls, peer benchmarks, speciality routing), and{' '}
                <strong data-id="element-100" data-mp="2lwe8894ey3rj">Conversion & Loyalty</strong> (treatment journeys,
                role-based content, congress follow-up). Every Snak captures
                behavioural data that sharpens the next campaign.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-101" data-mp="1t8qrx6a6dzap">
                Platform Architecture
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-102" data-mp="226asse44ltks">
                Behind the product runs an AI-driven closed-loop architecture
                organized as Think · Create · Learn · Improve, built on three
                layers: the <strong data-id="element-103" data-mp="3dm98v1xq1fvi">AI Intelligence Engine</strong> (blueprint
                analysis, story generation, content creation, channel
                optimization), the <strong data-id="element-104" data-mp="hpuxlglwxrxq">Data Supply Chain</strong>{' '}
                (therapeutic-area themes, brand data, HCP insights, market
                intelligence), and the <strong data-id="element-105" data-mp="mrk7uu1ba1kr">Application Suite</strong> (Snak
                Composer, Snakker Player, Distribution, Analytics).
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-106" data-mp="vqp6s1gsbwxg">
                The Moat
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-107" data-mp="10nflolgusmha">
                Defensibility comes from four compounding assets:{' '}
                <strong data-id="element-108" data-mp="1af22cxbyndrf">medical storytelling</strong> as codified pharma
                expertise, a{' '}
                <strong data-id="element-109" data-mp="1feep7i2u0ku0">short-format interactive engagement platform</strong>{' '}
                driving measurable outcomes, deep{' '}
                <strong data-id="element-110" data-mp="1jgji2elghdfl">pharma distribution</strong> relationships, and a
                privacy-safe <strong data-id="element-111" data-mp="33y0yrif0z04y">behavioural dataset</strong> that compounds
                performance with every campaign. Together they form an
                Unbreakable Flywheel that no broad player can match inside this
                regulated vertical.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-112" data-mp="2py9g34dkcvkm">
                Why We're Raising
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-113" data-mp="3nl8jdxyrh1rh">
                Out of necessity,{' '}
                <strong data-id="element-114" data-mp="3in6vmzqe4e9o">
                  85% of our sales effort today goes to Stage I pilots
                </strong>
                . They close in 4–7 weeks and bring in cash, but cap at €5–10K —
                leaving the larger Stage II (€25–60K, 3–6 months), Stage III
                (€100K+, 4–8 months) and Stage IV (€1M+, 12+ months)
                opportunities sitting idle. Today's pipeline already includes{' '}
                <strong data-id="element-115" data-mp="1znza2tretdf6">9 named accounts in Stage I</strong> (Nestlé HS, CSL
                Vifor, Bayer, Abbott, AstraZeneca, MSD, Sanofi, Novartis,
                Boehringer), <strong data-id="element-116" data-mp="23p2r3jl71fhz">4 in Stage II</strong> (Solventum,
                Menarini, Roche, Leo Pharma), and{' '}
                <strong data-id="element-117" data-mp="1pxxoc8mj473f">GSK in early Stage III</strong>. Investment buys the{' '}
                <strong data-id="element-118" data-mp="1uvpofpxbuzip">liquidity to run longer sales cycles</strong> and shift
                focus to <strong data-id="element-119" data-mp="1fyyptivh9ru5">10% / 20% / 30% / 40%</strong> across Stages I
                → IV. The plan is to{' '}
                <strong data-id="element-120" data-mp="1l0o0gxesg3hp">
                  move existing Stage I and II clients into Stage III, and Stage
                  III into Stage IV
                </strong>{' '}
                — same accounts, 10× the deal size.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-121" data-mp="31dcsbwd5nz81">
                Fast Marketing™ Revenue per Account
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-122" data-mp="36bo081jw3znm">
                ACV per pharma account is built on three list-price units that
                discount with volume: a{' '}
                <strong data-id="element-123" data-mp="3biulz1z1cgx9">Master Snak from €2,900 down to €1,900</strong> as
                campaign volume scales, country localization{' '}
                <strong data-id="element-124" data-mp="3khzik2r1e06s">from €999 down to €499</strong> (0% → 50% off as country
                count grows from 2 → 50+), and{' '}
                <strong data-id="element-125" data-mp="30ol63bh4izz">
                  data insights & hosting from €99/month per country
                </strong>{' '}
                with the same volume curve. Versus a traditional digital pharma
                campaign — banners, emails, WhatsApp GIFs plus strategy,
                creative, regulatory review and local adaptation — which costs{' '}
                <strong data-id="element-126" data-mp="2dtj3u43dmghk">€15,000–€19,000 per campaign per country</strong>, the
                Snakker model lands a fully managed, localized deployment in the{' '}
                <strong data-id="element-127" data-mp="29tif9qmkcows">low thousands</strong>. ACV scales predictably as each
                account adds brands, campaigns and countries.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-128" data-mp="33bfcu5xyyrrq">
                Financial Projections — 2026–2029
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-129" data-mp="3851k1ild63g5">
                Revenue scales{' '}
                <strong data-id="element-130" data-mp="3c63i3w3jo9gj">DKK 5.82M → 12.82M → 22.75M → 36M</strong> across
                2026–2029. The business turns{' '}
                <strong data-id="element-131" data-mp="2kckz90a8rxd5">profitable in 2028</strong> with a net result of{' '}
                <strong data-id="element-132" data-mp="2kexntwai95z6">+DKK 5.45M</strong>, growing to{' '}
                <strong data-id="element-133" data-mp="2odwov5t48ilc">+DKK 18.6M in 2029</strong>. 2026 and 2027 stay
                investment-mode (net results of −DKK 1.39M and −DKK 1.51M) as we
                fund the move into Stage II–IV deals.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-134" data-mp="3qjsd7231968">
                The Raise
              </h3>
              <p className="text-slate-700 leading-relaxed" data-id="element-135" data-mp="irqnwmngfjux">
                We are raising <strong data-id="element-136" data-mp="dse5ce3m8yy5">DKK 4M</strong>, allocated{' '}
                <strong data-id="element-137" data-mp="dsxjrku2557f">60% to Account Management</strong> (the sales capacity
                needed to push Stage II clients into Stage III and Stage III
                into Stage IV), <strong data-id="element-138" data-mp="50vm1ubdi30u">20% to AI Optimization</strong> and{' '}
                <strong data-id="element-139" data-mp="zhntyrk9kr">20% to Delivery Capability</strong>. The outcome is a
                scalable AI-native business with compounding learning dynamics
                and recurring runtime revenue.
              </p>

              <h3 className="text-base font-bold text-[#6C2BD9] uppercase tracking-wider mt-8 mb-2" data-id="element-140" data-mp="3m7o174gwx7xh">
                In One Line
              </h3>
              <p className="text-lg font-semibold text-slate-900 leading-snug mt-2 pb-4" data-id="element-141" data-mp="3i8f71yhyn81e">
                Snakker is Campaign Service Management for legacy pharma — and
                this raise is what moves our 14 named pharma accounts from
                pilots into the campaign-calendar model running today inside
                GSK, Solventum, Leo Pharma and Roche.
              </p>
            </article>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}